import { closestElement, getParentElement } from './select-elements.utils';
export const VIEWPORT_ATTRIBUTE = 'data-sy-viewport';
export const FLOATING_ELEMENT_CLASS_NAME = 'sy-floating-element';
class FloatingElements {
  constructor(viewportService) {
    this.viewportService = viewportService;
    this.elementsInfos = new Map();
  }
  /**
   * Repositions the floating element to the closest viewport.
   * @param el Element reference
   */
  open(el) {
    const originalParent = getParentElement(el);
    const originalParentNode = originalParent.fromShadowRoot ? originalParent.el.shadowRoot : originalParent.el;
    const originalNextSiblingEl = el.nextElementSibling;
    const { containerEl } = this.viewportService.appendChild(el);
    const { removeListener } = addElementRemovedListener({
      parentNode: originalParentNode,
      nextSiblingEl: originalNextSiblingEl,
      listener: () => this.close(el),
    });
    this.elementsInfos.set(el, {
      originalParentNode,
      originalNextSiblingEl,
      beforeClose: async () => {
        await removeListener();
      },
      afterClose: () => {
        containerEl === null || containerEl === void 0 ? void 0 : containerEl.remove();
      },
    });
  }
  /**
   * Repositions the floating element, returning it to the original position in the DOM hierarchy
   * @param el Element reference
   */
  async close(el) {
    const floatingElementInfos = this.elementsInfos.get(el);
    if (!floatingElementInfos) {
      return console.error('Attempt to close a floating element that has not been opened.');
    }
    const { originalParentNode, originalNextSiblingEl, beforeClose, afterClose } = floatingElementInfos;
    this.elementsInfos.delete(el);
    await beforeClose();
    insertBefore(el, originalParentNode, originalNextSiblingEl);
    afterClose();
  }
}
const insertBefore = (el, parentNode, nextSiblingEl) => {
  try {
    parentNode.insertBefore(el, nextSiblingEl);
  }
  catch (err) {
    // If the original next sibling element is not child of original parent element
    // because the next sibling element has moved in the DOM
    if (err instanceof DOMException && err.code === DOMException.NOT_FOUND_ERR) {
      parentNode.appendChild(el);
    }
    else {
      throw err;
    }
  }
};
const addElementRemovedListener = ({ parentNode, nextSiblingEl, listener }) => {
  const floatedEl = document.createElement('sy-floated');
  floatedEl.style.display = 'none';
  floatedEl.addListeners({ afterRemoved: listener });
  insertBefore(floatedEl, parentNode, nextSiblingEl);
  return {
    removeListener: async () => {
      await floatedEl.removeListeners();
      floatedEl.remove();
    },
  };
};
class EncapsulatedStyles {
  getEncapsulatedStyles(el) {
    const rootNodeEl = el.getRootNode();
    const isEncapsulated = rootNodeEl.nodeType === Node.DOCUMENT_FRAGMENT_NODE && rootNodeEl instanceof ShadowRoot;
    if (!isEncapsulated) {
      return null;
    }
    const classList = this.getShadowRootClassList(rootNodeEl);
    const styleSheets = rootNodeEl.styleSheets;
    const adoptedStyleSheets = Object.assign({}, (rootNodeEl.adoptedStyleSheets || {}));
    if (!styleSheets) {
      console.error('Floating elements: Stylesheets not found.');
      return { styleEl: null, classList };
    }
    const styleEl = document.createElement('style');
    let cssText = this.getCssText(styleSheets) + '\n' + this.getCssText(adoptedStyleSheets);
    // Necessary to do not apply the host styles defined in the copied style sheets
    cssText = ':host { all: unset !important; } ' + cssText;
    styleEl.innerText = cssText;
    return { styleEl, classList };
  }
  getShadowRootClassList(shadowRoot) {
    const classList = [];
    shadowRoot.host.classList.forEach(className => {
      if (className === 'hydrated')
        return;
      classList.push(className);
    });
    return classList;
  }
  getCssText(styleSheets) {
    return Object.values(styleSheets).map(this.mapStyleSheetToCssText.bind(this)).join('');
  }
  mapStyleSheetToCssText(styleSheet) {
    return Object.values(styleSheet.cssRules)
      .map(cssRule => cssRule.cssText)
      .join('');
  }
}
class FloatingElementViewport {
  constructor(encapsulatedStyles) {
    this.encapsulatedStyles = encapsulatedStyles;
  }
  appendChild(childEl) {
    var _a, _b;
    const viewportElement = this.findViewport(childEl) || document.body;
    const encapsulatedStyles = this.encapsulatedStyles.getEncapsulatedStyles(childEl);
    let containerEl = null;
    if (encapsulatedStyles) {
      // Encapsulates styles in a shadow-dom
      const floatingElementEl = document.createElement('sy-floating-element');
      containerEl = floatingElementEl;
      viewportElement.appendChild(floatingElementEl);
      encapsulatedStyles.styleEl && ((_a = floatingElementEl.shadowRoot) === null || _a === void 0 ? void 0 : _a.appendChild(encapsulatedStyles.styleEl));
      floatingElementEl.classList.add(...[...encapsulatedStyles.classList, FLOATING_ELEMENT_CLASS_NAME]);
      (_b = floatingElementEl.shadowRoot) === null || _b === void 0 ? void 0 : _b.appendChild(childEl);
    }
    else {
      viewportElement.appendChild(childEl);
    }
    return { viewportElement, containerEl };
  }
  findViewport(el) {
    const { el: parentElement } = getParentElement(el);
    return closestElement(`[${VIEWPORT_ATTRIBUTE}]`, parentElement);
  }
}
export const floatingElements = new FloatingElements(new FloatingElementViewport(new EncapsulatedStyles()));
